"use client";

import Link from "next/link";
import CodeMirror from "@uiw/react-codemirror";
// import { xml } from "@codemirror/lang-xml";
import { githubDark } from "@uiw/codemirror-theme-github";
import TrySeozastCTA from "@/app/components/TrySeozastCTA";
import { javascript } from "@codemirror/lang-javascript";

const Page = () => {
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-4">How to Create a Dynamic Sitemap in Next.js</h1>
        <p className="text-md mb-4">
          A sitemap is crucial for SEO, helping search engines crawl your Next.js site efficiently. This guide explains how to create a dynamic sitemap that automatically includes static and dynamic routes, such as articles, without manual updates. We’ll compare dynamic and static sitemaps and show why automation is key for scalability. For more SEO strategies, explore our guides on <Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">dynamic Markdown rendering</Link>, <Link href="/articles/what-is-pseo-and-seo" className="text-emerald-500 hover:underline">PSEO and SEO basics</Link>, <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation</Link>, and <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Dynamic vs. Static Sitemaps</h2>
        <p className="text-md mb-4">
          A <strong>dynamic sitemap</strong> programmatically generates URLs by scanning your site’s content, such as article directories, and updates automatically when new pages are added. This approach is ideal for sites with frequently changing content, like blogs or e-commerce platforms, as it eliminates manual updates, reduces errors, and ensures search engines always see the latest URLs. For example, the dynamic sitemap below scans the <code>app/articles</code> directory to include all article pages, complementing dynamic content rendering as shown in our <Link href="/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo" className="text-emerald-500 hover:underline">Markdown rendering guide</Link>.
        </p>
        <p className="text-md mb-4">
          In contrast, a <strong>static sitemap</strong> relies on hardcoded URLs, requiring manual edits whenever your site changes. This is manageable for small sites but becomes impractical for larger or frequently updated sites. Static sitemaps risk becoming outdated, missing new pages, or including deleted ones, which can harm SEO. Manual maintenance is also time-consuming, unlike the automated solutions offered by tools like SEOZast, as discussed in our <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Dynamic Sitemap Code Example</h2>
        <p className="text-md mb-4">
          Place this code in <code>app/sitemap.xml/route.ts</code> to generate a dynamic sitemap. It scans the <code>app/articles</code> directory for article pages and combines them with static URLs, excluding <code>changefreq</code> and <code>priority</code> for simplicity.
        </p>
        <CodeMirror
          value={`import { format } from "date-fns";
import fs from "fs";
import path from "path";

// Interface definitions
interface StaticUrl {
  path: string;
}

interface ArticlePage {
  slug: string;
  lastmod: Date;
}

const staticUrls: StaticUrl[] = [
  { path: "/" },
  { path: "/register" },
  { path: "/login" },
  { path: "/privacy-policy" },
  { path: "/terms-of-service" },
  { path: "/user-data-deletion" },
];

/**
 * Helper function to scan directories and find articles
 */
function scanDirectory(dir: string, parentPath: string = '', articles: ArticlePage[] = []): ArticlePage[] {
  const items = fs.readdirSync(dir);
  
  for (const item of items) {
    if (item.startsWith('[') || item === 'api') continue;
    
    const itemPath = path.join(dir, item);
    const stat = fs.statSync(itemPath);
    
    if (stat.isDirectory()) {
      const pageFile = path.join(itemPath, 'page.tsx');
      if (fs.existsSync(pageFile)) {
        const slug = parentPath ? \`\${parentPath}/\${item}\` : item;
        articles.push({
          slug,
          lastmod: stat.mtime
        });
      }
      
      const newParentPath = parentPath ? \`\${parentPath}/\${item}\` : item;
      scanDirectory(itemPath, newParentPath, articles);
    }
  }
  
  return articles;
}

/**
 * Function to get all article directories 
 */
async function getArticleSlugs(): Promise<ArticlePage[]> {
  try {
    const articlesDir = path.join(process.cwd(), "/app/articles");
    const articles = scanDirectory(articlesDir);
    return articles;
  } catch (error) {
    console.error("Error reading article slugs:", error);
    return [];
  }
}

export async function GET() {
  const baseUrl = "https://your-domain.com";
  const currentDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx");

  const staticEntries = staticUrls.map(
    ({ path }) => \`
      <url>
        <loc>\${baseUrl}\${path}</loc>
        <lastmod>\${currentDate}</lastmod>
      </url>\`
  );

  const articles = await getArticleSlugs();
  
  const articleEntries = articles.map(
    ({ slug, lastmod }: ArticlePage) => \`
      <url>
        <loc>\${baseUrl}/app/articles/\${slug}</loc>
        <lastmod>\${format(new Date(lastmod), "yyyy-MM-dd'T'HH:mm:ssxxx")}</lastmod>
      </url>\`
  );

  const sitemap = \`<?xml version="1.0" encoding="UTF-8"?>
  <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
    \${staticEntries.join("\n")}
    \${articleEntries.join("\n")}
  </urlset>\`;

  return new Response(sitemap, {
    status: 200,
    headers: {
      "Content-Type": "application/xml",
      "Cache-Control": "public, s-maxage=3600, stale-while-revalidate",
    },
  });
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="600px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This dynamic sitemap uses <code>fs</code> to scan the <code>app/articles</code> directory, automatically including new articles without manual intervention. It formats the output as XML, ensuring search engines can crawl all relevant pages.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Static Sitemap Code Example</h2>
        <p className="text-md mb-4">
          For comparison, here’s a static sitemap implementation in <code>app/sitemap.xml</code>. It hardcodes all URLs, requiring manual updates when pages are added or removed.
        </p>
        <CodeMirror
          value={`import { format } from "date-fns";

export async function GET() {
  const baseUrl = "https://your-domain.com";
  const currentDate = format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx");

  const sitemap = \`<?xml version="1.0" encoding="UTF-8"?>
  <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
    <url>
      <loc>\${baseUrl}/</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/register</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/login</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/privacy-policy</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/terms-of-service</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/user-data-deletion</loc>
      <lastmod>\${currentDate}</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/app/articles/first-article</loc>
      <lastmod>2025-01-01T00:00:00+00:00</lastmod>
    </url>
    <url>
      <loc>\${baseUrl}/app/articles/second-article</loc>
      <lastmod>2025-02-01T00:00:00+00:00</lastmod>
    </url>
  </urlset>\`;

  return new Response(sitemap, {
    status: 200,
    headers: {
      "Content-Type": "application/xml",
      "Cache-Control": "public, s-maxage=3600, stale-while-revalidate",
    },
  });
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="400px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This static sitemap lists all URLs manually, including a few article pages. If you add a new article or remove an existing one, you must update this file by hand, which is prone to errors and inefficient for large sites.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Why Choose Dynamic Sitemaps?</h2>
        <p className="text-md mb-4">
          Dynamic sitemaps save time and reduce errors by automatically reflecting your site’s structure. They’re essential for sites with frequent updates, ensuring search engines like Google always have an up-to-date map of your content. Static sitemaps, while simpler for small sites, lack flexibility and scalability. By implementing the dynamic sitemap above, you can enhance your Next.js site’s SEO with minimal maintenance. To further automate SEO tasks, consider tools like SEOZast, as explored in our <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link> and <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages guide</Link>.
        </p>
      </section>

      <TrySeozastCTA />
    </div>
  );
};

export default Page;